/**
 * Types and interfaces for TableSearch components
 */

// ============================================================================
// BASIC SEARCH TYPES
// ============================================================================

export interface BasicSearchProps {
  searchTerm: string;
  placeholder?: string;
  onSearchTermChange: (term: string) => void;
  onClearSearch?: () => void;
}

// ============================================================================
// ADVANCED SEARCH TYPES
// ============================================================================

/**
 * Supported data types for advanced search fields
 */
export type FieldDataType = 'string' | 'number' | 'date' | 'selection';

/**
 * Operator types based on field data type
 */
export type StringOperator = 'contains' | 'equals' | 'notEquals';
export type NumberOperator =
  | 'equals'
  | 'notEquals'
  | 'greaterThan'
  | 'lessThan';
export type DateOperator = 'isBefore' | 'isAfter' | 'isBetween' | 'equals';
export type SelectionOperator = 'equals' | 'notEquals';

export type FieldOperator =
  | StringOperator
  | NumberOperator
  | DateOperator
  | SelectionOperator;

/**
 * Logical operator for combining rules/groups
 */
export type LogicalOperator = 'AND' | 'OR';

/**
 * Configuration for a searchable field
 */
export interface FieldConfig {
  /** Unique key for the field (matches database column or API parameter) */
  key: string;
  /** Display label for the field */
  label: string;
  /** Data type of the field */
  dataType: FieldDataType;
  /** Options for selection fields (required if dataType is 'selection' and not using async) */
  options?: Array<{ label: string; value: string }>;
  /** API endpoint for async select fields (e.g., '/api/manufacturers') */
  apiEndpoint?: string;
  /** Field to search on in async select (e.g., 'name') */
  searchField?: string;
  /** Field to use as value in async select (e.g., 'id') */
  valueField?: string;
  /** Field to use as label in async select (e.g., 'name') */
  labelField?: string;
}

/**
 * A single search rule within a filter group
 */
export interface SearchRule {
  /** Unique identifier for the rule */
  id: string;
  /** Field key from FieldConfig */
  field: string;
  /** Operator for comparison */
  operator: FieldOperator;
  /** Value(s) to compare against */
  value: string | number | Date | [Date, Date];
}

/**
 * A group of search rules combined with a logical operator
 */
export interface FilterGroup {
  /** Unique identifier for the filter group */
  id: string;
  /** Logical operator to combine rules within this group */
  logicalOperator: LogicalOperator;
  /** Array of search rules in this group */
  rules: SearchRule[];
}

/**
 * Complete advanced search query structure
 */
export interface AdvancedSearchQuery {
  /** Array of filter groups */
  filterGroups: FilterGroup[];
  /** Logical operator to combine filter groups */
  groupOperator: LogicalOperator;
}

/**
 * Props for AdvancedSearch component
 */
export interface AdvancedSearchProps {
  /** Configuration defining available fields for searching */
  fieldConfigs: FieldConfig[];
  /** Current advanced search query */
  query: AdvancedSearchQuery;
  /** Callback when search button is clicked */
  onSearch: (query: AdvancedSearchQuery) => void;
  /** Callback when clear button is clicked */
  onClear: () => void;
}

// ============================================================================
// TABLE SEARCH TYPES (Main Component)
// ============================================================================

/**
 * Search mode type
 */
export type SearchMode = 'basic' | 'advanced' | 'basicOnly' | 'advancedOnly';

/**
 * Props for main TableSearch component
 */
export interface TableSearchProps {
  /** Current search mode */
  mode: SearchMode;
  /** Callback when search mode changes */
  onModeChange: (mode: SearchMode) => void;

  // Basic search props
  basicSearchTerm: string;
  onBasicSearchChange: (term: string) => void;
  onBasicSearchClear?: () => void;
  basicPlaceholder?: string;

  // Advanced search props
  advancedFieldConfigs: FieldConfig[];
  advancedQuery: AdvancedSearchQuery;
  onAdvancedSearch: (query: AdvancedSearchQuery) => void;
  onAdvancedClear: () => void;
}

// ============================================================================
// OPERATOR REGISTRY (Single Source of Truth)
// ============================================================================
// Backend SQL operators defined in api/sql/advancedSearchBuilder.js
// These are the only operators supported for filtering/searching

/**
 * Metadata for each supported operator
 */
interface OperatorMetadata {
  /** Internal operator value used in queries */
  value: FieldOperator;
  /** User-facing display label */
  label: string;
  /** SQL operator or condition type */
  sqlOperator: string;
  /** Supported data types for this operator */
  dataTypes: FieldDataType[];
}

/**
 * Centralized operator registry
 * These operators match exactly what the backend supports in advancedSearchBuilder.js
 */
export const OPERATOR_REGISTRY: Record<FieldOperator, OperatorMetadata> = {
  // String operators
  contains: {
    value: 'contains',
    label: 'Contains',
    sqlOperator: 'LIKE',
    dataTypes: ['string'],
  },
  equals: {
    value: 'equals',
    label: 'Equals',
    sqlOperator: '=',
    dataTypes: ['string', 'number', 'date', 'selection'],
  },
  notEquals: {
    value: 'notEquals',
    label: 'Not Equals',
    sqlOperator: '!=',
    dataTypes: ['string', 'number', 'selection'],
  },
  // Number operators
  greaterThan: {
    value: 'greaterThan',
    label: 'Greater Than',
    sqlOperator: '>',
    dataTypes: ['number'],
  },
  lessThan: {
    value: 'lessThan',
    label: 'Less Than',
    sqlOperator: '<',
    dataTypes: ['number'],
  },
  // Date operators
  isBefore: {
    value: 'isBefore',
    label: 'Before',
    sqlOperator: '<',
    dataTypes: ['date'],
  },
  isAfter: {
    value: 'isAfter',
    label: 'After',
    sqlOperator: '>',
    dataTypes: ['date'],
  },
  isBetween: {
    value: 'isBetween',
    label: 'Between',
    sqlOperator: 'BETWEEN',
    dataTypes: ['date'],
  },
};

/**
 * Operator display labels (derived from registry)
 * @deprecated Use OPERATOR_REGISTRY directly for label access
 */
export const OPERATOR_LABELS: Record<FieldOperator, string> = {
  contains: OPERATOR_REGISTRY.contains.label,
  equals: OPERATOR_REGISTRY.equals.label,
  notEquals: OPERATOR_REGISTRY.notEquals.label,
  greaterThan: OPERATOR_REGISTRY.greaterThan.label,
  lessThan: OPERATOR_REGISTRY.lessThan.label,
  isBefore: OPERATOR_REGISTRY.isBefore.label,
  isAfter: OPERATOR_REGISTRY.isAfter.label,
  isBetween: OPERATOR_REGISTRY.isBetween.label,
};

/**
 * Get available operators for a given field data type
 */
export function getOperatorsForDataType(
  dataType: FieldDataType
): FieldOperator[] {
  switch (dataType) {
    case 'string':
      return ['contains', 'equals', 'notEquals'];
    case 'number':
      return ['equals', 'notEquals', 'greaterThan', 'lessThan'];
    case 'date':
      return ['isBefore', 'isAfter', 'isBetween', 'equals'];
    case 'selection':
      return ['equals', 'notEquals'];
    default:
      return [];
  }
}

/**
 * Create an empty filter group
 */
export function createEmptyFilterGroup(): FilterGroup {
  return {
    id: `group_${Date.now()}`,
    logicalOperator: 'AND',
    rules: [],
  };
}

/**
 * Create an empty search rule
 */
export function createEmptySearchRule(): SearchRule {
  return {
    id: `rule_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    field: '',
    operator: 'equals',
    value: '',
  };
}

/**
 * Create an empty advanced search query
 */
export function createEmptyAdvancedQuery(): AdvancedSearchQuery {
  return {
    filterGroups: [createEmptyFilterGroup()],
    groupOperator: 'AND',
  };
}
